import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AnimeCard from "@/components/feature/AnimeCard";
import EmptyState from "@/components/base/EmptyState";
import LoadingState from "@/components/base/LoadingState";
import { animeCategories, animeList, animeRegions, animeStatuses, animeYears } from "@/mocks/anime";

const ratingOptions = [
  { label: "全部评分", value: 0 },
  { label: "9 分以上", value: 9 },
  { label: "8.5 分以上", value: 8.5 },
  { label: "8 分以上", value: 8 },
];

const sortOptions = [
  { label: "最近更新", value: "updated" },
  { label: "热度最高", value: "views" },
  { label: "名称 A-Z", value: "name-asc" },
  { label: "名称 Z-A", value: "name-desc" },
];

const PAGE_SIZE = 8;

interface FilterGroupProps {
  label: string;
  options: (string | number)[];
  value: string | number;
  onChange: (v: string | number) => void;
  renderLabel?: (v: string | number) => string;
}

function FilterGroup({ label, options, value, onChange, renderLabel }: FilterGroupProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 py-3 border-b border-background-200 last:border-b-0">
      <span className="text-sm font-semibold text-foreground-800 sm:w-16 shrink-0 sm:pt-1.5">
        {label}
      </span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = opt === value;
          return (
            <button
              key={String(opt)}
              type="button"
              onClick={() => onChange(opt)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                active
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {renderLabel ? renderLabel(opt) : String(opt)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category") || "全部";

  const [region, setRegion] = useState<string>("全部");
  const [year, setYear] = useState<string | number>("全部");
  const [status, setStatus] = useState<string>("全部");
  const [minRating, setMinRating] = useState<string | number>(0);
  const [sort, setSort] = useState<string>("updated");
  const [page, setPage] = useState(1);
  const [showFilter, setShowFilter] = useState(false);
  const [loading, setLoading] = useState(true);

  const selectedCategory = categoryParam;

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(timer);
  }, [selectedCategory, region, year, status, minRating, sort, page]);

  const setCategory = (c: string) => {
    const next = new URLSearchParams(searchParams);
    if (c === "全部") {
      next.delete("category");
    } else {
      next.set("category", c);
    }
    setSearchParams(next);
    setPage(1);
  };

  const filtered = useMemo(() => {
    const result = animeList.filter((a) => {
      if (selectedCategory !== "全部" && !a.categories.includes(selectedCategory)) return false;
      if (region !== "全部" && a.region !== region) return false;
      if (year !== "全部" && a.year !== Number(year)) return false;
      if (status !== "全部" && a.status !== status) return false;
      if (Number(minRating) > 0 && a.rating < Number(minRating)) return false;
      return true;
    });

    switch (sort) {
      case "views":
        return result.sort((a, b) => b.views - a.views);
      case "name-asc":
        return result.sort((a, b) => a.title.localeCompare(b.title, "zh-Hans-CN"));
      case "name-desc":
        return result.sort((a, b) => b.title.localeCompare(a.title, "zh-Hans-CN"));
      default:
        return result.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    }
  }, [selectedCategory, region, year, status, minRating, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setCategory("全部");
    setRegion("全部");
    setYear("全部");
    setStatus("全部");
    setMinRating(0);
    setSort("updated");
    setPage(1);
  };

  const categoryTitle = selectedCategory === "全部" ? "全部动漫" : `${selectedCategory}动漫`;

  return (
    <div className="w-full">
      <div className="w-full bg-primary-50 border-b border-primary-100">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-600 mb-3 flex-wrap">
            <Link to="/" className="hover:text-primary-700 cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-900 font-medium">{categoryTitle}</span>
          </nav>
          <h1 className="text-2xl md:text-4xl font-bold text-foreground-950">{categoryTitle}</h1>
          <p className="mt-2 text-sm md:text-base text-foreground-700 max-w-2xl leading-relaxed">
            汇集「{categoryTitle}」相关的优质资源，支持按类型、地区、年代、状态与评分精准筛选，
            快速找到你想看的作品。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        <div className="rounded-lg bg-background-50 border border-background-200 p-4 md:p-6">
          <div className="flex items-center justify-between md:hidden mb-2">
            <button
              type="button"
              onClick={() => setShowFilter((v) => !v)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-secondary-100 text-secondary-900 text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-filter-3-line"></i>
              筛选条件
              <i className={showFilter ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="text-xs text-primary-700 font-medium cursor-pointer whitespace-nowrap"
            >
              重置筛选
            </button>
          </div>

          <div className={`${showFilter ? "block" : "hidden"} md:block`}>
            <FilterGroup
              label="类型"
              options={["全部", ...animeCategories]}
              value={selectedCategory}
              onChange={(v) => setCategory(String(v))}
            />
            <FilterGroup
              label="地区"
              options={["全部", ...animeRegions]}
              value={region}
              onChange={(v) => {
                setRegion(String(v));
                setPage(1);
              }}
            />
            <FilterGroup
              label="年代"
              options={["全部", ...animeYears]}
              value={year}
              onChange={(v) => {
                setYear(v);
                setPage(1);
              }}
            />
            <FilterGroup
              label="状态"
              options={["全部", ...animeStatuses]}
              value={status}
              onChange={(v) => {
                setStatus(String(v));
                setPage(1);
              }}
            />
            <FilterGroup
              label="评分"
              options={ratingOptions.map((r) => r.value)}
              value={minRating}
              onChange={(v) => {
                setMinRating(v);
                setPage(1);
              }}
              renderLabel={(v) => ratingOptions.find((r) => r.value === v)?.label || String(v)}
            />
          </div>

          <div className="mt-4 pt-4 border-t border-background-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              共筛选出 <span className="font-bold text-primary-700">{filtered.length}</span> 部动漫
            </p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-600 whitespace-nowrap hidden sm:inline">排序</span>
              <select
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value);
                  setPage(1);
                }}
                className="h-9 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
              >
                {sortOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="mt-6 md:mt-8">
          {loading ? (
            <LoadingState label="正在筛选动漫…" />
          ) : paged.length === 0 ? (
            <EmptyState
              icon="ri-filter-off-line"
              title="没有匹配的动漫"
              description="试试调整或重置筛选条件，换个类型、地区或年份看看。"
              actionLabel="重置筛选"
              onAction={resetFilters}
            />
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {paged.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-2">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => setPage(currentPage - 1)}
                    className="w-9 h-9 rounded-md border border-background-300 flex items-center justify-center text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line text-lg"></i>
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPage(i + 1)}
                      className={`w-9 h-9 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap ${
                        currentPage === i + 1
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-50 border border-background-300 text-foreground-700 hover:border-primary-300"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() => setPage(currentPage + 1)}
                    className="w-9 h-9 rounded-md border border-background-300 flex items-center justify-center text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line text-lg"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}